import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatEUR, formatDate, statusClass } from "@/lib/format";
import { useTwoFactor } from "@/components/TwoFactorProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Receipt, FileText, ArrowLeft, Sparkles } from "lucide-react";

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [prds, setPrds] = useState([]);
  const [err, setErr] = useState("");
  const [genQuoteBusy, setGenQuoteBusy] = useState(false);
  const [genPrdBusy, setGenPrdBusy] = useState(false);
  const twoFactor = useTwoFactor();

  const load = useCallback(async () => {
    try {
      const [l, q, p] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get("/quotes"),
        api.get("/prds"),
      ]);
      setLead(l.data);
      setQuotes(q.data.filter((qq) => qq.lead_id === id));
      setPrds(p.data.filter((pp) => pp.lead_id === id));
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const genQuote = async () => {
    setGenQuoteBusy(true);
    try {
      await twoFactor.requireCode({
        actionLabel: `Genera preventivo per ${lead.company_name}`,
        run: async (code) => {
          const { data } = await api.post(
            `/leads/${id}/generate-quote`,
            {},
            { headers: { "X-Second-Factor": code } },
          );
          toast.success(`Preventivo ${data.id} generato · totale ${formatEUR(data.total_eur)}`);
          await load();
        },
      });
    } catch (_) {
      // cancel or handled
    } finally {
      setGenQuoteBusy(false);
    }
  };

  const genPrd = async () => {
    setGenPrdBusy(true);
    try {
      await twoFactor.requireCode({
        actionLabel: `Genera PRD per ${lead.company_name}`,
        run: async (code) => {
          const { data } = await api.post(
            `/leads/${id}/generate-prd`,
            {},
            { headers: { "X-Second-Factor": code } },
          );
          toast.success(`PRD ${data.id} generato · v${data.version}`);
          await load();
        },
      });
    } catch (_) {
      // handled
    } finally {
      setGenPrdBusy(false);
    }
  };

  if (err) {
    return (
      <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
        {err}
      </div>
    );
  }
  if (!lead) {
    return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;
  }

  return (
    <div data-testid={TID.leadDetail} className="space-y-6">
      <Link
        to="/app/leads"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED]"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
        Torna a Lead
      </Link>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-5 py-4 flex items-start justify-between bg-[#141414]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#0055FF]" strokeWidth={1.5} />
              lead · {lead.id}
            </div>
            <h2 className="font-heading uppercase text-2xl tracking-tight mt-1">{lead.company_name}</h2>
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${statusClass(lead.status)}`}>
            {lead.status}
          </span>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:divide-x divide-[#1F1F1F] divide-y md:divide-y-0">
          <InfoRow label="Settore" value={lead.sector} />
          <InfoRow label="Budget" value={formatEUR(lead.budget_eur)} mono />
          <InfoRow label="Email contatto" value={lead.contact_email || "—"} mono />
          <InfoRow label="Creato" value={formatDate(lead.created_at, { withTime: true })} mono />
          <div className="md:col-span-2 border-t border-[#1F1F1F] px-5 py-4">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">
              Esigenza
            </div>
            <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-line">{lead.need}</p>
          </div>
          {lead.notes && (
            <div className="md:col-span-2 border-t border-[#1F1F1F] px-5 py-4">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">Note</div>
              <p className="text-sm text-neutral-400">{lead.notes}</p>
            </div>
          )}
        </div>

        <div className="border-t border-[#1F1F1F] px-5 py-4 flex flex-wrap gap-3">
          <Button
            data-testid={TID.leadGenQuoteBtn}
            onClick={genQuote}
            disabled={genQuoteBusy}
            className="rounded-none border border-[#F5A623] bg-transparent hover:bg-[#F5A623] hover:text-black text-[#F5A623] font-mono text-xs uppercase tracking-widest"
          >
            <Receipt className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {genQuoteBusy ? "Generazione…" : "Genera Preventivo"}
          </Button>
          <Button
            data-testid={TID.leadGenPrdBtn}
            onClick={genPrd}
            disabled={genPrdBusy}
            className="rounded-none border border-[#F5A623] bg-transparent hover:bg-[#F5A623] hover:text-black text-[#F5A623] font-mono text-xs uppercase tracking-widest"
          >
            <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {genPrdBusy ? "Generazione…" : "Genera PRD"}
          </Button>
          <div className="font-mono text-[10px] text-neutral-500 self-center">
            → richiede secondo fattore (000000 in scaffold)
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ArtifactList
          title="Preventivi generati"
          icon={Receipt}
          items={quotes}
          emptyMsg="Nessun preventivo. Clicca 'Genera Preventivo'."
          render={(q) => ({
            id: q.id,
            title: q.project_name,
            meta: `${q.items.length} voci · ${formatEUR(q.total_eur)} · ${formatDate(q.created_at)}`,
            to: `/app/quotes/${q.id}`,
            status: q.status,
          })}
        />
        <ArtifactList
          title="PRD generati"
          icon={FileText}
          items={prds}
          emptyMsg="Nessun PRD. Clicca 'Genera PRD'."
          render={(p) => ({
            id: p.id,
            title: p.project_name,
            meta: `v${p.version} · ${p.milestones.length} milestone · ${formatDate(p.created_at)}`,
            to: `/app/prds/${p.id}`,
            status: p.status,
          })}
        />
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="px-5 py-4">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">
        {label}
      </div>
      <div className={mono ? "font-mono text-sm text-[#EDEDED]" : "text-sm text-[#EDEDED]"}>
        {value}
      </div>
    </div>
  );
}

function ArtifactList({ title, icon: Icon, items, emptyMsg, render }) {
  return (
    <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            {title}
          </span>
        </div>
        <span className="font-mono text-[10px] text-neutral-600">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="p-4 font-mono text-xs text-neutral-500">{emptyMsg}</div>
      ) : (
        <ul className="divide-y divide-[#1F1F1F]">
          {items.map((it) => {
            const r = render(it);
            return (
              <li key={r.id} className="hover:bg-[#141414]">
                <Link to={r.to} className="block px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-neutral-300">{r.id}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(r.status)}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#EDEDED] mt-1">{r.title}</div>
                  <div className="font-mono text-[10px] text-neutral-500 mt-1">{r.meta}</div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
